// AssetPage.js - Enhanced with REAL Yahoo Finance Historical Data
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import { AssetNewsPanel } from '../components/AssetNewsPanel';
import { MagmaNewsSection } from '../components/MagmaNewsSection';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

import ESGBadge from '../components/ESGBadge';


const API_URL      = process.env.REACT_APP_API_URL || '/api/go';
const DINO_API     = '/api/tickers61'; // worldwide scanner fallback
const NEWS_API_URL = process.env.REACT_APP_NEWS_API || '/api/news82';

// ============================================
// GO PROXY DATA FETCHER (TIER 3 STORAGE!)
// ============================================
const fetchYahooFinanceData = async (ticker, range = '1mo') => {
  try {
    // USE GO PROXY - NO CORS ISSUES!
    const url = `${API_URL}/api/yahoo/v2/${ticker}`;
    
    console.log(`📊 Fetching from Go Proxy (Tier 3): ${url}`);
    
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`❌ Go proxy error: ${response.status}`);
      // Fallback to DINO worldwide timeseries for international tickers
      try {
        const dinoResp = await fetch(`${DINO_API}/api/ticker/${ticker}`);
        if (!dinoResp.ok) return null;
        const d = await dinoResp.json();
        if (!d.ult) return null;
        const now = Math.floor(Date.now() / 1000);
        return {
          chartData: [
            { timestamp: now - 86400, date: 'Anterior', open: d.ult, high: d.ult, low: d.ult, close: d.ult, volume: d.vol || 0 },
            { timestamp: now,         date: 'Último',   open: d.ult, high: d.ult, low: d.ult, close: d.ult, volume: d.vol || 0 },
          ],
          meta: { ticker, lastUpdated: new Date().toISOString(), dataPoints: 2 },
        };
      } catch (e) { return null; }
    }
    
    const data = await response.json();
    
    console.log(`✅ Go Proxy Response:`, data);
    
    // Check if we got candles
    if (!data.candles || data.candles.length === 0) {
      console.error('❌ No candles in response');
      return null;
    }
    
    // Transform to chart-friendly format
    const chartData = data.candles.map(candle => ({
      timestamp: candle.ts,
      date: candle.date,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      volume: candle.volume
    })).filter(d => d.close !== null); // Remove null values
    
    console.log(`✅ Got ${chartData.length} data points from Tier 3 storage`);
    
    return {
      chartData,
      meta: {
        ticker: data.ticker,
        lastUpdated: data.last_updated,
        dataPoints: data.data_points
      }
    };
  } catch (err) {
    console.error('❌ Error fetching from Go proxy:', err);
    return null;
  }
};

const TICKER_TO_ESG_SLUG = {
  // Retail & Consumer
  'NTCO3': 'natura',
  'ARZZ3': 'arezzo',
  'MGLU3': 'magalu',
  'LREN3': 'lojas-renner',
  'AMER3': 'americanas',

  // Commodities & Industry
  'VALE3': 'vale',
  'PETR4': 'petrobras',
  'PETR3': 'petrobras',
  'CSNA3': 'csn',
  'GGBR4': 'gerdau',
  'SUZB3': 'suzano',
  'KLBN11': 'klabin',

  // Banking & Finance
  'ITUB4': 'itau',
  'BBDC4': 'bradesco',
  'BBAS3': 'banco-do-brasil',
  'SANB11': 'santander',
  'BPAC11': 'btg-pactual',

  // Utilities & Energy
  'ELET3': 'eletrobras',
  'ELET6': 'eletrobras',
  'CPLE6': 'copel',
  'EQTL3': 'equatorial',
  'SBSESP3': 'sabesp',
  'VBBR3': 'vibra',

  // Food & Healthcare
  'JBSS3': 'jbs',
  'BRFS3': 'brf',
  'RADL3': 'raia-drogasil',
  'HAPV3': 'hapvida'
};


// ============================================
// PRICE CHART WITH YAHOO DATA
// ============================================
const PriceChart = ({ ticker, theme }) => {
  const [allData, setAllData] = useState([]); // Store ALL data from API
  const [chartData, setChartData] = useState([]); // Filtered data for display
  const [selectedRange, setSelectedRange] = useState('max'); // DEFAULT TO MAX!
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const timeRanges = [
    { label: '1D', value: '1d', days: 1 },
    { label: '5D', value: '5d', days: 5 },
    { label: '1M', value: '1mo', days: 30 },
    { label: '3M', value: '3mo', days: 90 },
    { label: '6M', value: '6mo', days: 180 },
    { label: 'YTD', value: 'ytd', days: 'ytd' },
    { label: '1A', value: '1y', days: 365 },
    { label: 'MAX', value: 'max', days: 'max' },
  ];

  // Fetch ALL data once when ticker changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await fetchYahooFinanceData(ticker);
      if (data) {
        setAllData(data.chartData);
        setMeta(data.meta);
      }
      setLoading(false);
    };
    loadData();
  }, [ticker]);

  // Filter data when range changes
  useEffect(() => {
    if (!allData.length) return;

    const now = Math.floor(Date.now() / 1000);
    let filtered = [...allData];

    const selectedTimeRange = timeRanges.find(r => r.value === selectedRange);
    
    if (selectedTimeRange?.days === 'max') {
      // Show everything
      filtered = allData;
    } else if (selectedTimeRange?.days === 'ytd') {
      // Year to date - from Jan 1 of current year
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1).getTime() / 1000;
      filtered = allData.filter(d => d.timestamp >= yearStart);
    } else if (selectedTimeRange?.days) {
      // Last N days
      const cutoffTime = now - (selectedTimeRange.days * 24 * 60 * 60);
      filtered = allData.filter(d => d.timestamp >= cutoffTime);
    }

    console.log(`📊 Filtered to ${filtered.length} points for ${selectedRange}`);
    setChartData(filtered);
  }, [selectedRange, allData]);

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        border: `1px solid ${theme.border}`,
        borderRadius: '16px',
        padding: '2rem',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary }}>Carregando dados históricos...</div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div style={{
        background: theme.cardBg,
        border: `1px solid ${theme.border}`,
        borderRadius: '16px',
        padding: '2rem',
        minHeight: '400px'
      }}>
        <h3 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontWeight: 800, color: '#D4AF37', marginBottom: '1rem' }}>📈 Gráfico</h3>
        <div style={{
          height: '350px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontWeight: 400,
          color: theme.textSecondary,
          border: `2px dashed ${theme.border}`,
          borderRadius: '8px'
        }}>
          Dados não disponíveis
        </div>
      </div>
    );
  }

  const firstPrice = chartData[0]?.close || 0;
  const lastPrice = chartData[chartData.length - 1]?.close || 0;
  const priceChange = ((lastPrice - firstPrice) / firstPrice) * 100;
  const isPositive = priceChange >= 0;

  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '1.5rem',
      backdropFilter: 'blur(10px)'
    }}>
      {/* Header with Time Range Selector */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h3 style={{ 
            fontFamily: "'Roble', system-ui, sans-serif",
            fontSize: '1.2rem', 
            fontWeight: 800, 
            color: '#D4AF37',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            📈 Histórico de Preço
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }}>
              💾 TIER 3 STORAGE
            </span>
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.8rem', color: theme.textSecondary }}>
              {chartData.length} de {allData.length} pontos • {meta?.ticker || ticker}
            </span>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              background: 'rgba(212, 175, 55, 0.1)',
              color: '#D4AF37',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold'
            }}>
              {timeRanges.find(r => r.value === selectedRange)?.label}
            </span>
          </div>
        </div>

        {/* Time Range Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {timeRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => setSelectedRange(range.value)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                padding: '0.5rem 1rem',
                background: selectedRange === range.value 
                  ? 'rgba(212, 175, 55, 0.2)' 
                  : 'transparent',
                border: `1px solid ${selectedRange === range.value ? '#D4AF37' : theme.border}`,
                borderRadius: '6px',
                color: selectedRange === range.value ? '#D4AF37' : theme.textSecondary,
                fontSize: '0.8rem',
                fontWeight: selectedRange === range.value ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                if (selectedRange !== range.value) {
                  e.target.style.borderColor = '#D4AF37';
                  e.target.style.color = '#D4AF37';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedRange !== range.value) {
                  e.target.style.borderColor = theme.border;
                  e.target.style.color = theme.textSecondary;
                }
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.4}/>
              <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis 
            dataKey="date" 
            stroke={theme.textSecondary}
            fontSize={9}
            angle={-45}
            textAnchor="end"
            height={70}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis 
            stroke={theme.textSecondary}
            fontSize={10}
            domain={['dataMin - 1', 'dataMax + 1']}
            tickFormatter={(value) => `R$ ${value.toFixed(2)}`}
          />
          <Tooltip
            contentStyle={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}
            formatter={(value, name) => {
              if (name === 'close') return [`R$ ${value.toFixed(2)}`, 'Fechamento'];
              if (name === 'high') return [`R$ ${value.toFixed(2)}`, 'Máxima'];
              if (name === 'low') return [`R$ ${value.toFixed(2)}`, 'Mínima'];
              return [value, name];
            }}
          />
          <Area 
            type="monotone" 
            dataKey="close" 
            stroke="#D4AF37" 
            fill="url(#priceGrad)" 
            strokeWidth={3}
            name="close"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Stats Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '1rem',
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary }}>Mínima</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '1.1rem', fontWeight: 600, color: '#ef4444' }}>
            R$ {Math.min(...chartData.map(d => d.low)).toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary }}>Máxima</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '1.1rem', fontWeight: 600, color: '#4ade80' }}>
            R$ {Math.max(...chartData.map(d => d.high)).toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary }}>Variação</div>
          <div style={{ 
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: '1.1rem', 
            fontWeight: 600, 
            color: isPositive ? '#4ade80' : '#ef4444' 
          }}>
            {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary }}>52W High</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '1.1rem', fontWeight: 600, color: '#D4AF37' }}>
            R$ {(() => {
              // Calculate 52-week high from last 365 days
              const now = Math.floor(Date.now() / 1000);
              const oneYearAgo = now - (365 * 24 * 60 * 60);
              const last52Weeks = allData.filter(d => d.timestamp >= oneYearAgo);
              if (last52Weeks.length === 0) return 'N/A';
              const high52W = Math.max(...last52Weeks.map(d => d.high || 0));
              return high52W > 0 ? high52W.toFixed(2) : 'N/A';
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// VOLUME CHART
// ============================================
const VolumeChart = ({ ticker, theme }) => {
  const [allData, setAllData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await fetchYahooFinanceData(ticker);
      if (data) {
        setAllData(data.chartData);
        // Filter to last 30 days
        const now = Math.floor(Date.now() / 1000);
        const thirtyDaysAgo = now - (30 * 24 * 60 * 60);
        const filtered = data.chartData.filter(d => d.timestamp >= thirtyDaysAgo);
        setChartData(filtered);
      }
      setLoading(false);
    };
    loadData();
  }, [ticker]);

  if (loading || !chartData.length) {
    return null;
  }

  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '1.5rem',
      backdropFilter: 'blur(10px)'
    }}>
      <h3 style={{ 
        fontFamily: "'Roble', system-ui, sans-serif",
        fontSize: '1.2rem', 
        fontWeight: 800, 
        color: '#D4AF37', 
        marginBottom: '1.5rem' 
      }}>
        📊 Volume de Negociação (30 dias)
      </h3>

      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis 
            dataKey="date" 
            stroke={theme.textSecondary} 
            fontSize={9}
            angle={-45}
            textAnchor="end"
            height={60}
            interval="preserveStartEnd"
            minTickGap={30}
          />
          <YAxis 
            stroke={theme.textSecondary} 
            fontSize={10}
            tickFormatter={(value) => {
              if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
              if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
              return value;
            }}
          />
          <Tooltip
            contentStyle={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px'
            }}
            formatter={(value) => [value.toLocaleString(), 'Volume']}
          />
          <Bar dataKey="volume" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'rgba(59, 130, 246, 0.1)',
        borderRadius: '8px',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        fontWeight: 400,
        fontSize: '0.75rem',
        color: theme.textSecondary
      }}>
        💡 Volume médio: {(chartData.reduce((sum, d) => sum + d.volume, 0) / chartData.length).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const AssetPage = ({ theme: propTheme, isDark: propIsDark }) => {
  const { type, symbol } = useParams();
  const layout = useLayoutMaster();
  const [assetData, setAssetData] = useState(null);
  const [topTickers, setTopTickers] = useState([]);
  const [marketData, setMarketData] = useState(null);
  const [news, setNews] = useState([]);
  const [newsStatus, setNewsStatus] = useState('loading');
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(propIsDark ?? true);

  const theme = propTheme || {
    background: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(240, 240, 240, 0.9)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#555',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  useEffect(() => {
    fetchAssetData();
    fetchAssetNews();
    fetchTopTickers();
  }, [type, symbol]);

  const fetchTopTickers = async () => {
    try {
      const r = await fetch(`${API_URL}/api/overview`);
      if (!r.ok) return;
      const d = await r.json();
      const tickers = (d.tickers || d.stocks || []).slice(0, 12).map(t => ({
        ticker: t.ticker, name: t.name || t.ticker, ult: t.ult || 0, var: t.var || 0,
      }));
      setTopTickers(tickers);
      setMarketData(d);
    } catch (e) { /* non-critical */ }
  };

  const fetchAssetData = async () => {
    try {
      // Try B3 first; fall back to DINO for international tickers
      let response = await fetch(`${API_URL}/api/ticker/${symbol}`);
      if (!response.ok) {
        response = await fetch(`${DINO_API}/api/ticker/${symbol}`);
      }
      if (!response.ok) { setLoading(false); return; }
      const data = await response.json();
      setAssetData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching asset:', err);
      setLoading(false);
    }
  };

  const fetchAssetNews = async (retryCount = 0) => {
    try {
      const response = await fetch(`${NEWS_API_URL}/api/news/ticker/${symbol}`);
      
      if (response.status === 202) {
        setNewsStatus('fetching');
        if (retryCount < 3) {
          setTimeout(() => fetchAssetNews(retryCount + 1), 5000);
        }
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setNews(data.news || []);
        setNewsStatus('ready');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setNewsStatus('error');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark ? '#050505' : '#f0f0f0'
      }}>
        <div style={{ fontFamily: "'Roble', system-ui, sans-serif", textAlign: 'center', color: '#D4AF37' }}>
          <h2>Carregando {symbol}...</h2>
        </div>
      </div>
    );
  }

  if (!assetData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark ? '#050505' : '#f0f0f0'
      }}>
        <div style={{ fontFamily: "'Roble', system-ui, sans-serif", textAlign: 'center', color: '#ef4444' }}>
          <h2>Ativo não encontrado</h2>
        </div>
      </div>
    );
  }

  const isPositive = assetData.var >= 0;
  const esgSlug = TICKER_TO_ESG_SLUG[symbol.toUpperCase()];

  // Currency-aware price formatter — international assets show USD/JPY/etc, not R$
  const ccy = assetData.currency || 'BRL';
  const noDecimals = ['JPY', 'KRW', 'IDR', 'CLP', 'TWD'].includes(ccy);
  const fmtPrice = (v) => {
    if (v === undefined || v === null) return 'N/A';
    if (ccy === 'BRL') return `R$ ${v.toFixed(2)}`;
    return `${ccy} ${noDecimals ? Math.round(v) : v.toFixed(2)}`;
  };
  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#050505' : '#f0f0f0', position: 'relative' }}>
      {/* Silk Background — opacity 0.4 to match home page darkness */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', opacity: 0.4 }}>
        <Silk speed={31} scale={1.5} color="#6660fe" noiseIntensity={2} rotation={0.2} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)} topTickers={topTickers} marketData={marketData} />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: layout.isMobile ? '1rem' : '2rem' }}>
          
          {/* Back Button */}
          <button
            onClick={() => window.history.back()}
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              color: theme.textPrimary,
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
              fontWeight: 600,
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
              e.currentTarget.style.borderColor = '#D4AF37';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.border;
            }}
          >
            ← Voltar
          </button>

          {/* Asset Header */}
          <div style={{
            background: `linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(102, 96, 254, 0.1))`,
            border: `2px solid rgba(212, 175, 55, 0.3)`,
            borderRadius: '16px',
            padding: layout.isMobile ? '1.25rem' : '2rem',
            marginBottom: '2rem',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', flexDirection: layout.isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: layout.isMobile ? 'flex-start' : 'start', gap: layout.isMobile ? '1rem' : 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <h1 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: layout.isMobile ? '2rem' : '3rem', fontWeight: 800, color: '#D4AF37', margin: 0 }}>
                    {assetData.name}
                  </h1>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    background: 'rgba(212, 175, 55, 0.2)',
                    color: '#D4AF37',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    {getTypeLabel(type)}
                  </span>
                </div>
                <p style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '1.1rem', color: theme.textSecondary, margin: 0 }}>
                  {symbol}
                </p>
              </div>

              <div style={{ textAlign: layout.isMobile ? 'left' : 'right' }}>
                <div style={{
                  fontSize: layout.isMobile ? '2.2rem' : '3.5rem',
                  fontWeight: 600,
                  color: theme.textPrimary,
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  marginBottom: '0.25rem'
                }}>
                  {fmtPrice(assetData.ult)}
                </div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: layout.isMobile ? '1.3rem' : '1.8rem',
                  fontWeight: 600,
                  color: isPositive ? '#4ade80' : '#ef4444'
                }}>
                  {isPositive ? '▲' : '▼'} {isPositive ? '+' : ''}{assetData.var?.toFixed(2) || '0.00'}%
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1.5rem',
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px'
            }}>
              <StatItem label="Abertura" value={fmtPrice(assetData.abe)} theme={theme} />
              <StatItem label="Máxima" value={fmtPrice(assetData.max)} theme={theme} />
              <StatItem label="Mínima" value={fmtPrice(assetData.min)} theme={theme} />
              <StatItem label="Volume" value={formatVolume(assetData.vol)} theme={theme} />
            </div>
          </div>

          {/* Content Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: layout.isMobile ? '1fr' : '2fr 1fr',
            gap: '2rem'
          }}>
            
            {/* Left Column: Charts + ESG */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <PriceChart ticker={symbol} theme={theme} />
              <VolumeChart ticker={symbol} theme={theme} />

              {/* ESG integration */}
              {esgSlug && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: layout.isMobile ? '1.3rem' : '1.8rem', fontWeight: 800, color: theme.textPrimary, margin: 0 }}>
                      🌱 Sustentabilidade & Governança (ESG)
                    </h2>
                    <div style={{ height: '1px', flex: 1, background: `linear-gradient(to right, ${theme.border}, transparent)` }} />
                  </div>
                  <div style={{ display: 'inline-block', background: 'rgba(0,0,0,0.3)', borderRadius: '16px', padding: '4px' }}>
                    <ESGBadge slug={esgSlug} portalUrl="http://100.119.212.17:5173" />
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: News */}
            <AssetNewsPanel 
              news={news} 
              symbol={symbol} 
              theme={theme}
              status={newsStatus}
              onRetry={() => fetchAssetNews(0)}
            />
          </div>

          {/* RR Site editorial feed — filtered to this company, full width below the grid */}
          <div style={{ marginTop: '2rem' }}>
            <MagmaNewsSection
              ticker={symbol}
              companyName={assetData.name}
              label={`Acervo Editorial · ${symbol}`}
            />
          </div>

        </main>
      </div>
    </div>
  );
};

// ============================================
// HELPERS
// ============================================

const StatItem = ({ label, value, theme }) => (
  <div>
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.75rem', color: theme.textSecondary, marginBottom: '0.25rem' }}>
      {label}
    </div>
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '1.2rem', fontWeight: 600, color: theme.textPrimary }}>
      {value}
    </div>
  </div>
);

const getTypeLabel = (type) => {
  const labels = {
    'stock': 'Ação',
    'index': 'Índice',
    'currency': 'Moeda',
    'crypto': 'Criptomoeda',
    'commodity': 'Commodity'
  };
  return labels[type] || type;
};

const formatVolume = (volume) => {
  if (!volume) return 'N/A';
  if (volume >= 1e9) return `${(volume / 1e9).toFixed(2)}B`;
  if (volume >= 1e6) return `${(volume / 1e6).toFixed(2)}M`;
  if (volume >= 1e3) return `${(volume / 1e3).toFixed(2)}K`;
  return volume.toString();
};

export default AssetPage;